import { PageDataTable } from "components/PageDataTable/PageDataTable";
import { centsToDollars, parsePrice } from "helpers/price";
import { Column } from "primereact/column";
import { DataTable, DataTablePFSEvent } from "primereact/datatable";
import { Dropdown } from "primereact/dropdown";
import { Message } from "primereact/message";
import { Tag } from "primereact/tag";
import { useState } from "react";
import { useGetFinixSettlementsQuery } from "store/queries/finixOps";
import {
  FinixHoldReasonCode,
  FinixReleaseStatus,
  IFinixMerchantItem,
  IFinixSettlementItem,
} from "types/FinixOps";

import { formatDateTime } from "../../utils/hstImports";

const PAGE_SIZE = 25;

const RELEASE_LABEL: Record<FinixReleaseStatus, string> = {
  RELEASED: "Released",
  NO_ENTRIES: "Nothing ready",
  HELD: "All held",
  FAILED: "Failed",
};

const RELEASE_SEVERITY: Record<
  FinixReleaseStatus,
  "success" | "danger" | "warning" | "info"
> = {
  RELEASED: "success",
  NO_ENTRIES: "info",
  HELD: "warning",
  FAILED: "danger",
};

// CLE-2934: why a fee-bearing split was held back from the release.
export const HOLD_REASON_LABEL: Record<FinixHoldReasonCode, string> = {
  FEE_NOT_READY: "fee queued but not ready",
  FEE_UNRESOLVED: "fee could not be linked to its split",
  NO_FEE_QUEUED: "no fee queued yet",
};

const SETTLEMENT_SEVERITY: Record<
  string,
  "success" | "danger" | "warning" | "info"
> = {
  APPROVED: "success",
  AWAITING_APPROVAL: "warning",
  PENDING: "info",
  FUNDING_FAILED: "danger",
};

const money = (cents: number | null) =>
  cents === null || cents === undefined
    ? "—"
    : parsePrice(centsToDollars(cents));

const merchantName = (m: { providerName: string | null; merchantId: string }) =>
  m.providerName || "Unknown provider";

const holdReasonsText = (
  reasons: Partial<Record<FinixHoldReasonCode, number>>
) =>
  (Object.keys(reasons) as FinixHoldReasonCode[])
    .filter((code) => (reasons[code] ?? 0) > 0)
    .map((code) => `${reasons[code]} ${HOLD_REASON_LABEL[code] ?? code}`)
    .join("; ");

function MerchantReleaseCell({ merchant }: { merchant: IFinixMerchantItem }) {
  const release = merchant.lastRelease;
  if (!release) return <span className="text-600">No release recorded</span>;
  const reasons = holdReasonsText(release.heldReasonCounts || {});
  return (
    <div className="flex flex-column gap-1">
      <div className="flex align-items-center gap-2">
        <Tag
          value={RELEASE_LABEL[release.status] ?? release.status}
          severity={RELEASE_SEVERITY[release.status]}
        />
        <small className="text-600">{formatDateTime(release.runAt)}</small>
      </div>
      <small>
        {release.entryCount} released
        {release.heldCount > 0 || release.heldFeeCount > 0
          ? `, ${release.heldCount} split(s) and ${release.heldFeeCount} fee(s) held`
          : ""}
      </small>
      {reasons ? (
        <small
          className="text-orange-700"
          data-testid={`finix-hold-reasons-${merchant.merchantId}`}
        >
          Held: {reasons}
        </small>
      ) : null}
    </div>
  );
}

function MerchantQueueCell({ merchant }: { merchant: IFinixMerchantItem }) {
  const { queue } = merchant;
  if (!queue) return <span className="text-600">Not checked yet</span>;
  return (
    <div className="flex flex-column gap-1">
      <small>{queue.pending} pending</small>
      {queue.staleOver25Days > 0 ? (
        <Message
          severity="warn"
          text={`${queue.staleOver25Days} older than 25 days (expire at 30)`}
          data-testid={`finix-stale-queue-${merchant.merchantId}`}
        />
      ) : null}
      {queue.staleAwaitingApproval > 0 ? (
        <Message
          severity="warn"
          text={`${queue.staleAwaitingApproval} awaiting approval over 24 h at Finix`}
        />
      ) : null}
      <small className="text-600">as of {formatDateTime(queue.asOf)}</small>
    </div>
  );
}

const releaseBody = (m: IFinixMerchantItem) => (
  <MerchantReleaseCell merchant={m} />
);

const queueBody = (m: IFinixMerchantItem) => <MerchantQueueCell merchant={m} />;

const settlementStatusBody = (s: IFinixSettlementItem) => (
  <div className="flex flex-column gap-1 align-items-start">
    <Tag value={s.status} severity={SETTLEMENT_SEVERITY[s.status] ?? "info"} />
    {s.warnings.includes("AWAITING_APPROVAL_OVER_24H") ? (
      <small
        className="text-orange-700"
        data-testid={`finix-settlement-warning-${s.id}`}
      >
        Awaiting approval over 24 h
      </small>
    ) : null}
  </div>
);

export function FinixSettlementsSection() {
  const [merchantId, setMerchantId] = useState<string | undefined>(undefined);
  const [page, setPage] = useState(1);
  const { data, isFetching } = useGetFinixSettlementsQuery({
    merchantId,
    page,
    pageSize: PAGE_SIZE,
  });
  const result = data?.data;
  const merchants = result?.merchants || [];

  const merchantOptions = [
    // `null`, not `undefined`: PrimeReact passes the option object for an undefined value (dev QA 10-08).
    { label: "All merchants", value: null },
    ...merchants.map((m) => ({
      label: `${merchantName(m)} (${m.merchantId})`,
      value: m.merchantId,
    })),
  ];

  const onPage = (event: DataTablePFSEvent) => {
    setPage(Math.floor(event.first / event.rows) + 1);
  };

  return (
    <div className="flex flex-column gap-3">
      {result && result.awaitingApprovalOver24h > 0 ? (
        <Message
          severity="warn"
          text={`${result.awaitingApprovalOver24h} settlement(s) awaiting approval for over 24 hours.`}
        />
      ) : null}

      <div className="flex flex-column gap-2">
        <span className="font-semibold">Per merchant</span>
        <small className="text-600">
          Last weekly release
          {result?.lastReleaseRun
            ? ` (${formatDateTime(result.lastReleaseRun.startedAt)})`
            : ""}{" "}
          and the settlement queue as the daily monitor last saw it.
        </small>
        <DataTable
          value={merchants}
          dataKey="merchantId"
          loading={isFetching}
          emptyMessage="No Finix merchants."
          responsiveLayout="scroll"
        >
          <Column header="Merchant" field="merchantId" />
          <Column
            header="Provider"
            body={(m: IFinixMerchantItem) => merchantName(m)}
          />
          <Column header="Account" field="accountName" />
          <Column header="Last release" body={releaseBody} />
          <Column header="Settlement queue" body={queueBody} />
        </DataTable>
      </div>

      <div className="flex flex-wrap align-items-center justify-content-between gap-3">
        <span className="font-semibold">Settlements</span>
        <Dropdown
          value={merchantId}
          options={merchantOptions}
          onChange={(e) => {
            setMerchantId(e.value || undefined);
            setPage(1);
          }}
          placeholder="All merchants"
          style={{ minWidth: "260px" }}
          aria-label="Filter settlements by merchant"
        />
      </div>
      <PageDataTable
        value={result?.items || []}
        dataKey="id"
        lazy
        paginator
        first={(page - 1) * PAGE_SIZE}
        rows={PAGE_SIZE}
        totalRecords={result?.total || 0}
        onPage={onPage}
        loading={isFetching}
        emptyMessage="No Finix settlements."
      >
        <Column header="Merchant" field="merchantId" />
        <Column
          header="Provider"
          body={(s: IFinixSettlementItem) => merchantName(s)}
        />
        <Column header="Account" field="accountName" />
        <Column header="Settlement" field="finixSettlementId" />
        <Column header="Status" body={settlementStatusBody} />
        <Column
          header="Total"
          body={(s: IFinixSettlementItem) => money(s.totalAmountInCents)}
        />
        <Column
          header="Net"
          body={(s: IFinixSettlementItem) => money(s.netAmountInCents)}
        />
        <Column
          header="Created"
          body={(s: IFinixSettlementItem) => formatDateTime(s.createdAt)}
        />
        <Column
          header="Approved"
          body={(s: IFinixSettlementItem) => formatDateTime(s.approvedAt)}
        />
      </PageDataTable>
    </div>
  );
}
